import {
    Box,
    Flex,
    HStack,
    IconButton,
    Button,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    useDisclosure,
    useColorModeValue,
    Stack,
    useColorMode,
    Text,
    keyframes,
    Avatar,
} from "@chakra-ui/react";
import { BsSun, BsMoonStarsFill } from "react-icons/bs";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import Resume from "../Components/Akshay_Bombatkar_Resume.pdf";

const slideDown = keyframes`
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const underlineGrow = keyframes`
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
`;

const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Background", href: "#education-experience" },
    { name: "Skills", href: "#skills" },
    { name: "Projects", href: "#projects" },
    { name: "Contact", href: "#contact" },
];

export default function Navbar({ onToggleTheme }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { colorMode } = useColorMode();
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState("home");

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);

            // Update active section based on scroll position
            const sections = navLinks.map(link => link.href.replace("#", ""));
            for (const section of sections.reverse()) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 100) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const bgColorLight = scrolled ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.8)";
    const bgColorDark = scrolled ? "rgba(17, 24, 39, 0.95)" : "rgba(17, 24, 39, 0.8)";
    const bgColor = useColorModeValue(bgColorLight, bgColorDark);
    
    const borderColorScrolled = useColorModeValue("gray.200", "gray.700");
    const borderColorTransparent = "transparent";
    const borderColor = scrolled ? borderColorScrolled : borderColorTransparent;
    
    const mobileMenuBg = useColorModeValue("rgba(255,255,255,0.98)", "rgba(17,24,39,0.98)");
    const mobileMenuBorder = useColorModeValue("gray.200", "gray.700");
    
    const navLinkActive = useColorModeValue("blue.600", "blue.400");
    const navLinkInactive = useColorModeValue("gray.600", "gray.400");
    const navLinkHoverColor = useColorModeValue("blue.600", "blue.400");
    const navLinkHoverBg = useColorModeValue("blue.50", "rgba(59, 130, 246, 0.1)");
    
    const mobileNavLinkActive = useColorModeValue("blue.600", "blue.400");
    const mobileNavLinkInactive = useColorModeValue("gray.700", "gray.300");
    const mobileNavLinkHoverBg = useColorModeValue("gray.100", "gray.700");
    
    const iconButtonHoverBg = useColorModeValue("gray.100", "gray.700");
    const avatarMenuBorder = useColorModeValue("gray.200", "gray.600");

    return (
        <>
            <Box
                id="nav-menu"
                bg={bgColor}
                px={4}
                position="fixed"
                w="100%"
                top={0}
                zIndex="99"
                backdropFilter="blur(12px)"
                borderBottom="1px solid"
                borderColor={borderColor}
                boxShadow={scrolled ? "0 4px 20px rgba(0,0,0,0.1)" : "none"}
                transition="all 0.3s ease"
                animation={`${slideDown} 0.5s ease-out`}
            >
                <Flex
                    h={16}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    maxW="1400px"
                    mx="auto"
                    px={{ base: 2, md: 4 }}
                >
                    {/* Mobile Menu Button */}
                    <IconButton
                        size={"md"}
                        icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
                        aria-label={"Open Menu"}
                        display={{ md: "none" }}
                        onClick={isOpen ? onClose : onOpen}
                        variant="ghost"
                        _hover={{ bg: iconButtonHoverBg }}
                    />

                    {/* Logo */}
                    <HStack spacing={3} alignItems={"center"}>
                        <Text 
                            fontSize={{ base: "lg", md: "xl" }} 
                            fontWeight={700}
                            bgGradient="linear(to-r, blue.500, purple.500)"
                            bgClip="text"
                        >
                            Akshay
                        </Text>
                    </HStack>

                    {/* Desktop Navigation */}
                    <Flex alignItems={"center"} gap={2}>
                        <HStack
                            as={"nav"}
                            spacing={1}
                            display={{ base: "none", md: "flex" }}
                        >
                            {navLinks.map((link, index) => (
                                <Box
                                    key={link.name}
                                    as="a"
                                    href={link.href}
                                    px={4}
                                    py={2}
                                    rounded="lg"
                                    fontSize="sm"
                                    fontWeight={600}
                                    color={activeSection === link.href.replace("#", "") ? navLinkActive : navLinkInactive}
                                    position="relative"
                                    transition="all 0.3s ease"
                                    _hover={{
                                        color: navLinkHoverColor,
                                        bg: navLinkHoverBg,
                                    }}
                                    animation={`${fadeIn} 0.5s ease-out ${index * 0.05}s both`}
                                >
                                    {link.name}
                                    {activeSection === link.href.replace("#", "") && (
                                        <Box
                                            position="absolute"
                                            bottom="2px"
                                            left="50%"
                                            transform="translateX(-50%)"
                                            width="20px"
                                            height="2px"
                                            bg="blue.500"
                                            borderRadius="full"
                                            animation={`${underlineGrow} 0.3s ease-out`}
                                        />
                                    )}
                                </Box>
                            ))}
                            
                            {/* Resume Button */}
                            <Button
                                as="a"
                                href={Resume}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => window.open(
                                    "https://drive.google.com/file/d/1GOj_2HXTBDILQ1xfynzR-T8wWc0mRbkH/view?usp=drive_link",
                                    "_blank"
                                )}
                                download="Akshay-Bombatkar-Resume"
                                size="sm"
                                colorScheme="blue"
                                variant="solid"
                                ml={2}
                                borderRadius="full"
                                px={6}
                                _hover={{
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                                }}
                                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                            >
                                Resume
                            </Button>
                        </HStack>

                        {/* Theme Toggle */}
                        <IconButton
                            aria-label="Toggle Color Mode"
                            onClick={onToggleTheme}
                            icon={colorMode === "dark" ? <BsSun /> : <BsMoonStarsFill />}
                            variant="ghost"
                            rounded="full"
                            ml={2}
                            _hover={{
                                bg: iconButtonHoverBg,
                                transform: "rotate(20deg)",
                            }}
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        />

                        {/* Avatar Menu - Desktop Only */}
                        <Menu>
                            <MenuButton
                                as={Button}
                                rounded={"full"}
                                variant={"link"}
                                cursor={"pointer"}
                                minW={0}
                                display={{ base: "none", md: "flex" }}
                                ml={2}
                            >
                                <Avatar
                                    size="sm"
                                    src="https://avatars.githubusercontent.com/u/107979339?v=4"
                                    border="2px solid"
                                    borderColor={avatarMenuBorder}
                                    display={{ base: "none", md: "block" }}
                                    transition="all 0.3s ease"
                                    _hover={{
                                        borderColor: "#60A5FA",
                                        transform: "scale(1.1)",
                                    }}
                                />
                            </MenuButton>
                            <MenuList>
                                <MenuItem as="a" href="https://github.com/bakshay96" target="_blank">
                                    GitHub Profile
                                </MenuItem>
                                <MenuItem as="a" href="https://linkedin.com/in/akshay-bombatkar" target="_blank">
                                    LinkedIn
                                </MenuItem>
                                <MenuDivider />
                                <MenuItem as="a" href="mailto:akshaymali.edu@gmail.com">
                                    Email Me
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </Flex>
                </Flex>

                {/* Mobile Navigation Drawer */}
                {isOpen && (
                    <Box
                        pb={6}
                        display={{ md: "none" }}
                        position="fixed"
                        top="64px"
                        left={0}
                        right={0}
                        bg={mobileMenuBg}
                        backdropFilter="blur(12px)"
                        borderBottom="1px solid"
                        borderColor={mobileMenuBorder}
                        boxShadow="0 10px 40px rgba(0,0,0,0.2)"
                        zIndex={100}
                        animation={`${slideDown} 0.3s ease-out`}
                    >
                        <Stack
                            as={"nav"}
                            spacing={1}
                            p={4}
                        >
                            {navLinks.map((link, index) => (
                                <Box
                                    key={link.name}
                                    as="a"
                                    href={link.href}
                                    onClick={onClose}
                                    py={3}
                                    px={4}
                                    rounded="lg"
                                    fontSize="lg"
                                    fontWeight={600}
                                    color={activeSection === link.href.replace("#", "") ? mobileNavLinkActive : mobileNavLinkInactive}
                                    bg={activeSection === link.href.replace("#", "") ? navLinkHoverBg : "transparent"}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                    _hover={{
                                        bg: mobileNavLinkHoverBg,
                                        color: mobileNavLinkActive,
                                        transform: "translateX(10px)",
                                    }}
                                    animation={`${fadeIn} 0.3s ease-out ${index * 0.05}s both`}
                                >
                                    {link.name}
                                </Box>
                            ))}
                            
                            <Box pt={4} px={4}>
                                <Button
                                    as="a"
                                    href={Resume}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => {
                                        window.open(
                                            "https://drive.google.com/file/d/1GOj_2HXTBDILQ1xfynzR-T8wWc0mRbkH/view?usp=drive_link",
                                            "_blank"
                                        );
                                        onClose();
                                    }}
                                    download="Akshay-Bombatkar-Resume"
                                    width="full"
                                    colorScheme="blue"
                                    size="lg"
                                    borderRadius="xl"
                                    leftIcon={<Text fontSize="xl">📄</Text>}
                                >
                                    Download Resume
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                )}
            </Box>
        </>
    );
}
